import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './api';
import type { Program } from './types';

export type DocType =
  | 'DELIVERABLE'
  | 'WORKING_PAPER'
  | 'CLIENT_PROVIDED'
  | 'VENDOR_MATERIAL'
  | 'MEETING_RECORD'
  | 'PUBLISHED_REPORT'
  | 'INTERNAL';

export interface DocVersion {
  version: number;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
  superseded?: boolean;
}

export interface Doc {
  id: string;
  title: string;
  type: DocType;
  tags: string[];
  internal: boolean;
  phaseId: string | null;
  taskId: string | null;
  deliverableId: string | null;
  createdAt: string;
  createdById: string | null;
  current: DocVersion | null;
  versions: DocVersion[];
}

export const ATTESTATION = 'This file contains no borrower, loan-level or personal information';
export const ACCEPT = '.pdf,.docx,.xlsx,.pptx,.csv,.txt,.md,.png,.jpg,.jpeg';
export const MAX_BYTES = 52_428_800;

export function docTypeLabel(t: DocType, program: Pick<Program, 'clientShortName'>) {
  return {
    DELIVERABLE: 'Deliverable',
    WORKING_PAPER: 'Working paper',
    CLIENT_PROVIDED: `${program.clientShortName}-provided`,
    VENDOR_MATERIAL: 'Vendor material',
    MEETING_RECORD: 'Meeting record',
    PUBLISHED_REPORT: 'Published report',
    INTERNAL: 'Internal',
  }[t];
}

export interface DocFilter {
  phaseId?: string;
  taskId?: string;
  deliverableId?: string;
  type?: string;
  q?: string;
  awaiting?: boolean;
}

export const docsKey = (programId: string, f: DocFilter = {}) => ['documents', programId, f] as const;

export function useDocuments(programId: string, f: DocFilter = {}, enabled = true) {
  return useQuery({
    queryKey: docsKey(programId, f),
    enabled,
    queryFn: () => {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(f)) if (v) p.set(k, v === true ? '1' : String(v));
      return api.get<Doc[]>(`/api/programs/${programId}/documents?${p}`);
    },
  });
}

/**
 * Multipart upload. Sends the CSRF header; leaves Content-Type to the browser so the
 * multipart boundary is set correctly.
 */
export async function uploadDocument(programId: string, form: FormData): Promise<{ id: string; version: number }> {
  const res = await fetch(`/api/programs/${programId}/documents`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-HELM': '1', Accept: 'application/json' },
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'ERROR', e.message ?? 'Upload failed.', e.details);
  }
  return data;
}

/** A signed link that expires in five minutes; the server records the view or download. */
export async function versionLink(
  programId: string,
  docId: string,
  version: number,
  disposition: 'inline' | 'attachment',
) {
  const r = await api.post<{ url: string }>(`/api/programs/${programId}/documents/${docId}/versions/${version}/link`, {
    disposition,
  });
  return r.url;
}

export async function downloadVersion(programId: string, docId: string, version: number) {
  const url = await versionLink(programId, docId, version, 'attachment');
  const a = Object.assign(document.createElement('a'), { href: url, rel: 'noopener' });
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export const canPreview = (mime: string) => mime === 'application/pdf' || mime.startsWith('image/');

export function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
